import { cn } from '@/lib/utils';

export interface CoverageStep {
  key: string;
  label: string;
  done: boolean;
  isNext: boolean;
  href?: string;
}

interface CoverageProgressStepperProps {
  steps: CoverageStep[];
  onStepClick?: (step: CoverageStep) => void;
}

export const CoverageProgressStepper = ({
  steps,
  onStepClick,
}: CoverageProgressStepperProps) => {
  // Calculate progress percentage based on the FURTHEST completed step
  // This handles cases where earlier steps may be incomplete but later steps are done
  // (e.g., review step shows incomplete but open claim, LOR, etc. are all done)
  const furthestDoneIndex = steps.reduce(
    (maxIdx, step, idx) => (step.done ? idx : maxIdx),
    -1,
  );
  const allDone = steps.every((s) => s.done);
  // Progress fills to center of the step AFTER the furthest completed step
  // +1 to get to next step index, +0.5 to center within that step = +1.5
  // If no steps are done, center on first step (0.5 / length)
  const progressPercent = allDone
    ? 100
    : furthestDoneIndex === -1
      ? (0.5 / steps.length) * 100
      : ((furthestDoneIndex + 1.5) / steps.length) * 100;

  return (
    <div className="w-full overflow-x-auto">
      {/* Progress bar */}
      <div className="relative h-1.5 w-full rounded-full bg-zinc-100 overflow-hidden min-w-[400px]">
        <div
          className="absolute left-0 top-0 h-full bg-violet-600 transition-all duration-300"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      {/* Step labels - using grid for equal spacing like StatusSummaryCard */}
      <div
        className="mt-2 min-w-[400px]"
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${steps.length}, 1fr)`,
        }}
      >
        {steps.map((step) => (
          <button
            key={step.key}
            type="button"
            onClick={() => onStepClick?.(step)}
            className={cn(
              'text-xs font-medium text-center transition-colors leading-tight',
              step.done && 'text-zinc-800',
              step.isNext &&
                'text-violet-600 font-semibold cursor-pointer hover:text-violet-700',
              !step.done && !step.isNext && 'text-zinc-600',
              'hover:underline underline-offset-2 ',
            )}
            disabled={!step.isNext && !step.href}
          >
            {step.label}
          </button>
        ))}
      </div>
    </div>
  );
};
